import React from "react";
import { createPortal } from "react-dom";
import { ArrowUpLeft, Bell, ChevronLeft, ChevronRight, RotateCw } from "lucide-react";

import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";

// Adresse de l'installation ouverte : chaque client a la sienne.
const SITE = typeof window === "undefined" ? "" : window.location.host;
const SiteIcon = ({ className }) => <img src="/favicon.ico?v=20261007-transia-arrow-v4" alt="" className={className} />;

// Schéma de la fenêtre de Chrome : la question apparaît en haut à gauche, sous l'adresse ; « Autoriser » est encerclé.
function ChromePromptSketch() {
  return (
    <div aria-hidden="true" className="overflow-hidden rounded-[4px] border border-slate-300 bg-slate-50 text-[10px] text-slate-600">
      <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-200 px-2 pt-1">
        {[0, 1, 2].map((dot) => <span key={dot} className="h-1.5 w-1.5 rounded-full bg-slate-400" />)}
        <span className="ml-2 flex h-4 items-center gap-1 rounded-t-[3px] bg-white px-1.5 text-[9px] font-bold text-slate-700">
          <SiteIcon className="h-2.5 w-2.5" /> TransIA
        </span>
      </div>
      <div className="flex items-center gap-1.5 border-b border-slate-200 bg-white px-2 py-1">
        <ChevronLeft className="h-3 w-3 text-slate-400" />
        <ChevronRight className="h-3 w-3 text-slate-400" />
        <RotateCw className="h-2.5 w-2.5 text-slate-400" />
        <span className="flex h-4 flex-1 items-center gap-1 rounded-full bg-slate-100 px-1.5 text-[9px] text-slate-600">
          <SiteIcon className="h-2.5 w-2.5" /> https://{SITE}/
        </span>
      </div>
      <div className="relative h-[92px] bg-white">
        <div className="absolute left-2 top-1.5 w-[178px] rounded-[4px] border border-slate-200 bg-white px-2 py-1.5 shadow-[0_6px_16px_rgba(15,23,42,0.18)]">
          <div className="truncate font-bold text-slate-800">{SITE} souhaite</div>
          <div className="mt-0.5 flex items-center gap-1"><Bell className="h-2.5 w-2.5" /> Afficher les notifications</div>
          <div className="mt-1.5 flex justify-end gap-1.5">
            <span className="rounded-full px-2 py-0.5 font-bold text-slate-800 ring-2 ring-[#c2410c] ring-offset-1">Autoriser</span>
            <span className="rounded-full border border-slate-200 px-2 py-0.5 text-slate-500">Bloquer</span>
          </div>
        </div>
        <span className="absolute left-[194px] top-[58px] font-extrabold text-[#c2410c]">← Cliquez ici</span>
      </div>
    </div>
  );
}

// Avant la question de Chrome : où elle va apparaître et quoi répondre. Après « Bloquer », Chrome ne la repose plus.
export default function DesktopAlertPermissionGuide({ waiting, onContinue, onCancel }) {
  React.useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape" && !waiting) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [waiting, onCancel]);
  const guide = (
    <div data-desktop-permission-guide="true" className="fixed inset-0 z-[calc(var(--z-index-modal)+30)] grid place-items-center bg-slate-900/35 p-4"
      onClick={waiting ? undefined : onCancel}>
      <section role="dialog" aria-modal="true" aria-label="Autoriser les avis Windows" onClick={(event) => event.stopPropagation()}
        className="w-[min(400px,100%)] rounded-[4px] border border-slate-200 bg-white p-4 shadow-[0_24px_60px_rgba(15,23,42,0.28)]">
        <h2 className="m-0 flex items-center gap-1.5 text-sm font-extrabold text-slate-900">
          {waiting ? <><ArrowUpLeft aria-hidden="true" className="h-4 w-4 text-[#c2410c]" strokeWidth={3} /> Répondez à Chrome, en haut à gauche</> : "Autoriser les avis Windows"}
        </h2>
        <p className="m-0 mt-1 text-[12.5px] leading-5 text-slate-600">
          {waiting
            ? <>Cliquez <b className="text-slate-900">« Autoriser »</b> dans la question de Chrome, en haut à gauche de la fenêtre.</>
            : <>Chrome va vous demander la permission en haut à gauche de la fenêtre. Cliquez <b className="text-slate-900">« Autoriser »</b>.</>}
        </p>
        <div className="mt-3"><ChromePromptSketch /></div>
        <p className="m-0 mt-2 text-[11.5px] leading-4 text-slate-500">Après « Bloquer », Chrome ne repose plus la question.</p>
        {waiting ? null : (
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={onCancel}
              className="inline-flex h-8 items-center rounded-[4px] border border-slate-300 bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50">Annuler</button>
            <button type="button" autoFocus onClick={onContinue}
              className="inline-flex h-8 items-center rounded-[4px] bg-accent px-3 text-xs font-extrabold text-white hover:bg-accent-strong">Continuer</button>
          </div>
        )}
      </section>
    </div>
  );
  const root = getReactPortalRoot();
  return root instanceof HTMLElement ? createPortal(guide, root) : guide;
}
